import React, { useState, useEffect, useRef } from "react";
import { IAppPropsWithRoute } from "../../../common/types";
import { withRouter } from "react-router-dom";
import { useDispatch } from "react-redux";
import Navbar from "../../shared/Navbar";
import { MuiThemeProvider, Theme, Paper, CircularProgress, Typography } from "@material-ui/core";
import ErrorTryAgain from "../../shared/ErrorTryAgain/ErrorTryAgain";
import SMAButton from "../../shared/Button/Button";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../../store/api";
import themeDark from "../../shared/themeDark";
import { showNavbar } from "../../../store/modules/appStateSlice";

interface HealthSharePageProps extends IAppPropsWithRoute {}

const useStyles = makeStyles((theme: Theme) => ({
  root: (theme: Theme) => ({
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0,
    boxShadow: "none",
    flex: "1 0",
  }),
  progress: (theme: Theme) => ({
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0,
    flex: "1 0",
    width: "100%",
  }),
  startStopButton: {
    marginTop: "22px",
  },
  timer: {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: 400,
    margin: "0",
  },
  headerTitle: {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: 400,
    margin: "19px 0 0 29px",
  },
}));

const formatTime = (timer: number) => {
  const getSeconds = `0${timer % 60}`.slice(-2);
  const getMinutes = `0${Math.floor(timer / 60) % 60}`.slice(-2);
  const getHours = `0${Math.floor(timer / 3600)}`.slice(-2);

  return `${getHours} : ${getMinutes} : ${getSeconds}`;
};

const HealthSharePage: React.FC<HealthSharePageProps> = () => {
  const classes = useStyles(themeDark);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isShiftStarted, setIsShiftStarted] = useState(false);
  const [shiftId, setShiftId] = useState("");
  const [timer, setTimer] = useState(0);
  const increment = useRef<null | ReturnType<typeof setTimeout>>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      // clear timer on unmount
      if (increment.current) {
        clearInterval(increment.current);
      }
    };
  }, []);

  useEffect(() => {
    (async function () {
      try {
        dispatch(showNavbar(false));
        const resp = await api.getActiveHealthShareShiftDetails("");

        if (resp?.message?.includes("found an active healthshare shift")) {
          const respShiftId = resp?.healthshareShiftId;
          setShiftId(respShiftId);
          await fetchShiftDetails(respShiftId);
          setIsShiftStarted(true);
          increment.current = setInterval(() => {
            setTimer((timer) => timer + 1);
          }, 1000);
        }
        setIsLoading(false);
      } catch (e) {
        setIsError(true);
      }
    })();
  }, []);

  const fetchShiftDetails = async (rshiftId: string) => {
    const shiftDetails = await api.getHealthshareShiftDetails(`healthshareShiftId=${rshiftId}`);
    const timeOnshift = shiftDetails?.model?.shiftDuration?.totalMilliseconds;
    setTimer(Math.round(timeOnshift / 1000));
  };

  const startStopShiftAction = async () => {
    if (!isShiftStarted) {
      // start shift action
      setIsLoading(true);
      try {
        // BE call to start shift
        const resp = await api.startHealthshareShift("", {});

        const isError = resp.hasError;
        if (isError) {
          setErrorText(resp.message);
          throw new Error();
        }

        const shift = resp?.healthshareShiftId;
        setShiftId(shift);
        setTimer(0);

        if (resp?.message?.includes("found an active healthshare shift")) {
          await fetchShiftDetails(shift);
        }
        setIsShiftStarted(true);
        increment.current = setInterval(() => {
          setTimer((timer) => timer + 1);
        }, 1000);
      } catch (e) {
        setIsError(true);
      }
      setIsLoading(false);
    } else {
      // stop shift action
      setIsLoading(true);
      try {
        // BE call to stop shift
        const resp = await api.endHealthshareShift("", {
          healthshareShiftId: shiftId,
        });
        setIsShiftStarted(false);
        if (increment.current) {
          clearInterval(increment.current);
        }
      } catch (e) {
        setIsError(true);
      }
      setIsLoading(false);
    }
  };

  const EventShiftTitle = (
    <Typography variant="h1" className={classes.headerTitle}>
      HealthShare Shift
    </Typography>
  );

  return (
    <MuiThemeProvider theme={themeDark}>
      <Navbar />
      {isLoading && (
        <Paper className={classes.progress}>
          <CircularProgress />
        </Paper>
      )}
      {isError && <ErrorTryAgain label={errorText} />}
      {!isLoading && !isError && (
        <Paper className={classes.root}>
          {isShiftStarted && (
            <Typography variant="h1" className={classes.timer}>
              On shift for {formatTime(timer + 1)}
            </Typography>
          )}
          <SMAButton className={classes.startStopButton} color="primary" variant="contained" onAction={startStopShiftAction}>
            {isShiftStarted ? "End Shift" : "Start Shift"}
          </SMAButton>
        </Paper>
      )}
    </MuiThemeProvider>
  );
};

export default withRouter(HealthSharePage);
